import React, { useEffect, useState, useMemo, useCallback } from "react";
import { UilSearch } from "@iconscout/react-unicons";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllTags,
  getBlogByTitle,
  getTags,
} from "../../redux/blog/blogSlice";

/**
 * TagItem Component
 * Single Responsibility - renders a single tag with count
 */
const TagItem = ({ item, count, onClick }) => (
  <div
    className="w-full flex items-center justify-between border-dotted 
               border-b border-gray-200 pb-2 sm:pb-3 
               hover:bg-gray-50 px-2 rounded transition-colors"
  >
    <button
      type="button"
      className="text-sm sm:text-base text-gray-700 hover:text-indigo-600 
                 hover:underline cursor-pointer transition-colors text-left"
      onClick={(e) => onClick(e, item)}
    >
      {item}
    </button>
    <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 
                     px-2 py-0.5 rounded-full">
      {count}
    </span>
  </div>
);

/**
 * TagButton Component
 * Single Responsibility - renders a selectable tag button
 */
const TagButton = ({ item, isSelected, onClick }) => (
  <button
    type="button"
    className={`
      px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm
      rounded-full border transition-all duration-200
      ${isSelected 
        ? "bg-indigo-500 text-white border-indigo-500" 
        : "bg-white text-gray-700 border-gray-300 hover:bg-indigo-50 hover:border-indigo-300"
      }
    `}
    onClick={(e) => onClick(e, item)}
  >
    {item}
  </button>
);

/**
 * SideSearchBooksAndPublications Component
 * Follows Composition pattern with smaller, focused components
 */
const SideSearchBooksAndPublications = ({ handleChangeTags, postType }) => {
  const dispatch = useDispatch();
  const tags = useSelector(getAllTags);
  
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchText, setSearchText] = useState("");

  // Get unique tags
  const uniqueTags = useMemo(() => 
    tags?.length > 0 ? [...new Set(tags)] : [],
    [tags]
  );

  // Calculate tag count summary
  const tagCountSummary = useMemo(() => {
    if (!tags?.length || !uniqueTags.length) return [];
    return uniqueTags.map(tag => ({
      item: tag,
      count: tags.filter(t => t === tag).length
    }));
  }, [tags, uniqueTags]);

  // Fetch tags when post type changes
  useEffect(() => {
    dispatch(getTags(postType));
    setSelectedTags([]);
    setSearchText("");
  }, [postType, dispatch]);

  // Event handlers
  const handleSelectTagsWithCount = useCallback((e, item) => {
    e.preventDefault();
    handleChangeTags([item]);
  }, [handleChangeTags]);

  const handleSelectTags = useCallback((e, item) => {
    e.preventDefault();
    
    if (item === "All") {
      setSelectedTags(["All"]);
      handleChangeTags(["All"]);
      return;
    }

    setSelectedTags(prev => {
      let newTags;
      if (prev.includes(item)) {
        newTags = prev.filter(x => x !== item);
      } else {
        newTags = prev.includes("All") 
          ? [item] 
          : [...prev, item];
      }
      handleChangeTags(newTags.length ? newTags : ["All"]);
      return newTags;
    });
  }, [handleChangeTags]);

  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearchText(value);
    dispatch(getBlogByTitle({ type: postType, title: value }));
  }, [dispatch, postType]);

  const isTagSelected = useCallback((item) => 
    selectedTags.includes(item),
    [selectedTags]
  );

  return (
    <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 lg:p-6 space-y-4 sm:space-y-6">
      {/* Search Input */}
      <div className="bg-gray-100 flex items-center gap-2 px-4 py-2.5 sm:py-3 rounded-full">
        <input
          type="text"
          placeholder="Search..."
          className="flex-1 bg-transparent border-none outline-none 
                     text-sm sm:text-base text-gray-700 placeholder-gray-500"
          value={searchText}
          onChange={handleSearch}
        />
        <UilSearch className="text-gray-500 w-5 h-5" />
      </div>

      {/* Post Categories */}
      <section>
        <h3 className="bg-indigo-600 py-2 sm:py-2.5 text-center text-white 
                       text-sm sm:text-base font-medium rounded-t-lg">
          Post Categories
        </h3>
        <div className="mt-3 space-y-2 sm:space-y-3 max-h-[200px] sm:max-h-[250px] 
                       overflow-y-auto pr-1 scrollbar-thin">
          {tagCountSummary.length > 0 ? (
            tagCountSummary.map((item, index) => (
              <TagItem
                key={index}
                item={item.item}
                count={item.count}
                onClick={handleSelectTagsWithCount}
              />
            ))
          ) : (
            <p className="text-center text-gray-500 text-sm py-4">
              No categories found
            </p>
          )}
        </div>
      </section>

      {/* Tags */}
      <section>
        <h3 className="bg-indigo-600 py-2 sm:py-2.5 text-center text-white 
                       text-sm sm:text-base font-medium rounded-t-lg">
          Tags
        </h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <TagButton
            item="All"
            isSelected={isTagSelected("All")}
            onClick={handleSelectTags}
          />
          {uniqueTags.map((item, index) => (
            <TagButton
              key={index}
              item={item}
              isSelected={isTagSelected(item)}
              onClick={handleSelectTags}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default SideSearchBooksAndPublications;
